import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import { IAdvertiserFormData, IAdvertiserGridRow, IAdvertiserPayload } from '@/Interfaces/Advertiser.interface';
import ModalHeaderIcon from '@/components/Icons/ModalHeaderIcon';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import ModalBody from '@/components/Modal/common/ModalBody';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import { DEFAULT_ADVERTISER } from '@/constants/advertisers';
import { handleApiValidationErrors } from '@/helpers/form';
import { createAdvertiser, updateAdvertiser } from '@/store/api/advertisers.api';
import { useAppSelector } from '@/store/hooks';
import { selectAdvertisersCategories } from '@/store/selectors/advertisers.selectors';
import { selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { Button, Modal } from '@mui/material';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import AdvertiserInfo from './components/AdvertiserInfo';

interface IProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  selectedAdvertiser?: IAdvertiserGridRow | null;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const AdvertiserModal = ({ open, onClose, selectedAdvertiser, loading, setLoading }: IProps) => {
  const categories = useAppSelector(selectAdvertisersCategories);
  const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.ADVERTISERS));

  const advertiser = useMemo<IAdvertiserFormData | null>(() => {
    if (!selectedAdvertiser) {
      return null;
    }
    return {
      ...selectedAdvertiser,
      domain: selectedAdvertiser.domain || '',
      category: categories.find(category => category.value === selectedAdvertiser.category) || null,
    };
  }, [selectedAdvertiser, categories]);

  const form = useForm<IAdvertiserFormData>({
    defaultValues: advertiser || DEFAULT_ADVERTISER,
  });

  const { handleSubmit, reset, setError } = form;

  useEffect(() => {
    if (advertiser) {
      reset(advertiser);
    } else {
      reset(DEFAULT_ADVERTISER);
    }
  }, [advertiser, reset]);

  const handleSubmitError = useCallback(
    (error: unknown) => {
      if (isAxiosError(error) && error.response?.data?.errors?.length) {
        handleApiValidationErrors(error.response.data.errors, setError);
        return;
      }

      if (isAxiosError(error)) {
        toast.error(error?.response?.data?.message || 'Unknown Internal Error');
      } else {
        toast.error((error as Error).message || 'Something went wrong');
      }
    },
    [setError],
  );

  const updateAdvertiserData = useCallback(
    async (id: number, payload: IAdvertiserPayload) => {
      setLoading(true);
      try {
        await updateAdvertiser(id, payload);
        toast.success('Advertiser updated successfully');
        reset();
        return true;
      } catch (error) {
        handleSubmitError(error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, reset, handleSubmitError],
  );

  const createNewAdvertiser = useCallback(
    async (payload: IAdvertiserPayload) => {
      setLoading(true);
      try {
        await createAdvertiser(payload);
        toast.success('Advertiser created successfully');
        reset();
        return true;
      } catch (error) {
        handleSubmitError(error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, reset, handleSubmitError],
  );

  const onSubmit = useCallback(
    async ({ id, name, client, agency, category, domain }: IAdvertiserFormData) => {
      const payload: IAdvertiserPayload = {
        name,
        domain,
        clientId: client!.value,
        agencyId: agency?.value || null,
        category: category!.value,
      };
      const isSaved =
        id && selectedAdvertiser ? await updateAdvertiserData(id, payload) : await createNewAdvertiser(payload);
      if (isSaved) {
        onClose(true);
      }
    },
    [createNewAdvertiser, updateAdvertiserData, selectedAdvertiser, onClose],
  );

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  return (
    <LoadingBackdropWrapper backdropOpen={loading}>
      <Modal open={open} onClose={handleClose} aria-labelledby='modal-title'>
        <ModalBody>
          <ModalTitleCard
            titleIcon={<ModalHeaderIcon />}
            handleClose={handleClose}
            title={selectedAdvertiser ? 'View Advertiser Details' : 'Create New Advertiser'}
          />
          <FormProvider {...form}>
            <form className='mt-4' onSubmit={handleSubmit(onSubmit)}>
              <AdvertiserInfo isCreate={!selectedAdvertiser} />
              <hr className='-mx-[32px] my-5' />
              <div className='mt-5 flex justify-end gap-[12px]'>
                <Button color='inherit' variant='outlined' onClick={handleClose}>
                  Cancel
                </Button>
                <Button color='primary' variant='contained' type='submit' disabled={readOnly}>
                  {selectedAdvertiser ? 'Save' : 'Create'}
                </Button>
              </div>
            </form>
          </FormProvider>
        </ModalBody>
      </Modal>
    </LoadingBackdropWrapper>
  );
};

export default AdvertiserModal;
